import { createAction } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import { l } from '@manychat/manyui'

import { MessagingType } from 'apps/posting/constants/MessagingType'
import PostModel from 'apps/posting/models/Post'
import { sendingStatsAdapter } from 'apps/posting/models/Post/adapter'
import { getPostSettingsChangesAdjustedForConfiguredChannels } from 'apps/posting/models/Post/adjustPostSettingsForConfiguredChannels'
import { IPost } from 'apps/posting/postingInterfaces'
import * as postSelectors from 'apps/posting/selectors/postSelectors'
import { billing, UpgradeSource } from 'common/billing'
import { IBuilderChannelsInfo } from 'common/builder/builderInterfaces'
import { MessageTagType } from 'common/builder/constants/facebookMessaging'
import InitialNodesPresetType from 'common/builder/constants/InitialNodesPresetType'
import * as builderSelectors from 'common/builder/selectors/builder/builderStateSelectors'
import { getFirstAgentNodeWithChannelBeforeChannelNode } from 'common/builder/selectors/builder/nodeSelectors'
import { alert } from 'common/core'
import { isFilterContainSmartSegment } from 'common/filter/models/AudienceFilter/helpers'
import { BroadcastingApi } from 'shared/api/requests/broadcasting'
import { Filter, Post, PostSendingStats } from 'shared/api/requests/broadcasting/schemas'
import { createAsyncAction } from 'shared/lib/redux/createAsyncAction'
import { navigatePollyfill } from 'utils/router/tools'
import { analyticsService } from 'utils/services/analytics'
import { anotherTabNotificationsListener } from 'utils/services/notificationsService'
import { linkURL } from 'utils/url'

export const addPost = createAsyncAction(
  'postActions/addPost',
  async (postData: Post, { getState }) => {
    const initial = PostModel.adapter(postData)
    const item = PostModel.create(initial)

    const existingItem = postSelectors.getById(getState(), item.id)
    if (existingItem && (existingItem.stats || existingItem.sendingStats)) {
      item.stats = existingItem.stats
      item.sendingStats = existingItem.sendingStats
    }

    return item
  },
)

export const copyPost = createAsyncAction(
  'postActions/copyPost',
  async (
    { postId, options = {} }: { postId: string | number; options?: { silent?: boolean } },
    { dispatch },
  ) => {
    const response = await BroadcastingApi.copy({ query: { post_id: postId } })

    const item = await dispatch(addPost(response.data.post)).unwrap()

    if (!options.silent) {
      alert(l.translate('Copied to Drafts!'), 'success')
    }

    return item
  },
  {
    onBusinessError: (error) => {
      if (billing.isProError(error)) {
        billing.handleProError(error, {
          source: UpgradeSource.DUPLICATE_BROADCAST,
        })
      }
    },
  },
)

export const copyPostAndOpen = createAsyncAction(
  'postActions/copyPostAndOpen',
  async (
    { postId, options = {} }: { postId: string | number; options?: { silent?: boolean } },
    { dispatch },
  ) => {
    const item = await dispatch(copyPost({ postId, options })).unwrap()

    if (item) {
      const url = linkURL(`/posting/drafts/${item.id}`)
      navigatePollyfill(url)
    }
  },
)

export const createPost = createAsyncAction(
  'postActions/createPost',
  async (flow_ns: string | undefined, { dispatch }) => {
    const response = await BroadcastingApi.create(flow_ns ? { body: { flow_ns } } : undefined)
    return await dispatch(addPost(response.data.post)).unwrap()
  },
  {
    autoHandleByGuards: [BroadcastingApi.create.isPostError],
  },
)

const resetPost = createAsyncAction(
  'postActions/resetPost',
  async (
    {
      postData,
      additionalChanges = {},
    }: { postData: Post; additionalChanges?: Record<string, unknown> },
    { dispatch, getState },
  ) => {
    const { id, ...otherChanges } = PostModel.adapter(postData)
    const changes = Object.assign({}, otherChanges, additionalChanges)

    const item = postSelectors.getById(getState(), id)
    const itemHasStats = item && (item.stats || item.sendingStats)
    const changesAffectStats = has(changes, 'stats') || has(changes, 'sendingStats')
    if (itemHasStats && !changesAffectStats) {
      changes.stats = item.stats
      changes.sendingStats = item.sendingStats
    }

    dispatch(updatePost(id, changes))
  },
)

export const schedulePost = createAsyncAction(
  'postActions/schedulePost',
  async (postId: number, { dispatch, getState }) => {
    const response = await BroadcastingApi.schedule({ query: { post_id: postId } })

    dispatch(resetPost({ postData: response.data.post }))
    return postSelectors.getById(getState(), postId)
  },
  {
    autoHandleByGuards: [BroadcastingApi.schedule.isPostError],
  },
)

export const unschedulePost = createAsyncAction(
  'postActions/unschedulePost',
  async (postId: number, { dispatch, getState }) => {
    const response = await BroadcastingApi.unschedule({ query: { post_id: postId } })

    dispatch(resetPost({ postData: response.data.post }))
    return postSelectors.getById(getState(), postId)
  },
)

export const interruptPostSending = createAsyncAction(
  'postActions/interruptPostSending',
  async (postId: number, { dispatch, getState }) => {
    const response = await BroadcastingApi.interrupt({ query: { post_id: postId } })

    alert(l.translate('Sending process was stopped'), 'success')
    dispatch(
      resetPost({ postData: response.data.post, additionalChanges: { $interruptSending: true } }),
    )

    return postSelectors.getById(getState(), postId)
  },
)

export const forcePostSending = createAsyncAction(
  'postActions/forcePostSending',
  async (postId: number, { dispatch, getState }) => {
    const response = await BroadcastingApi.forceSending({ query: { post_id: postId } })

    alert(l.translate('All broadcast messages will be sent as soon as possible'), 'success')
    dispatch(
      resetPost({
        postData: response.data.post,
        additionalChanges: { $forceSendingInProgress: true },
      }),
    )
    return postSelectors.getById(getState(), postId)
  },
  {
    autoHandleByGuards: [BroadcastingApi.forceSending.isPostError],
  },
)

export const createPostAndOpen = createAsyncAction(
  'postActions/createPostAndOpen',
  async (
    {
      flow_ns,
      initialNodesPreset,
      params = {},
    }: {
      flow_ns?: string
      initialNodesPreset?: InitialNodesPresetType
      params?: Record<string, string>
    },
    { dispatch },
  ) => {
    const newItem = await dispatch(createPost(flow_ns)).unwrap()

    const searchParams = cloneDeep(params)

    if (initialNodesPreset) {
      searchParams['preset'] = initialNodesPreset
    }
    const searchString = new URLSearchParams(searchParams).toString()
    const queryString = searchString ? `?${searchString}` : ''

    if (newItem) {
      const url = linkURL(`/posting/drafts/${newItem.id}${queryString}`)
      navigatePollyfill(url)
    }

    return
  },
)

export const unscheduleAndOpen = createAsyncAction(
  'postActions/unscheduleAndOpen',
  async (postId: number, { dispatch }) => {
    const post = await dispatch(unschedulePost(postId)).unwrap()

    if (post) {
      const url = linkURL(`/posting/drafts/${post.id}`)
      navigatePollyfill(url)
    }
  },
)

export const updatePostSendingStats = createAsyncAction(
  'postActions/updatePostSendingStats',
  async (
    { postId, sendingStats }: { postId: number; sendingStats: PostSendingStats },
    { dispatch },
  ) => {
    const changes = {
      sendingStats: sendingStatsAdapter(sendingStats),
    }
    dispatch(updatePost(postId, changes))
  },
)

export const updatePostSettings = createAsyncAction(
  'postActions/updatePostSettings',
  async (
    { postId, changes = {} }: { postId: number | string; changes?: Partial<IPost> },
    { dispatch, getState },
  ) => {
    dispatch(updatePost(postId, changes))
    const post = postSelectors.getById(getState(), postId)
    const exportedPost = PostModel.exporter(post)
    const payload = {
      filter: exportedPost.filter as Filter,
      messaging_type: exportedPost.messaging_type,
      notification: exportedPost.notification,
      schedule: exportedPost.schedule,
      timezone: exportedPost.timezone,
      message_tag: exportedPost.message_tag,
      current_time: Number(new Date()) / 1000,
      current_time_offset: new Date().getTimezoneOffset(),
    }
    const hasSmartSegmentInCondition = isFilterContainSmartSegment(changes.filter)

    if (hasSmartSegmentInCondition) {
      analyticsService.sendEvent('SMART.SEGMENT.CONDITION.BROADCAST')
    }

    const response = await BroadcastingApi.update({
      body: payload,
      query: { post_id: postId },
    })

    dispatch(resetPost({ postData: response.data.post }))
  },
  {
    autoHandleByGuards: [BroadcastingApi.update.isFilterError, BroadcastingApi.update.isPostError],
  },
)

export const loadPost = createAsyncAction(
  'postActions/loadPost',
  async (postId: string | number, { dispatch }) => {
    const response = await BroadcastingApi.load({ query: { post_id: postId } })
    return dispatch(addPost(response.data.post))
  },
)

export const removePost = createAsyncAction(
  'postActions/removePost',
  async (postId: string | number, { dispatch, getState }) => {
    const response = await BroadcastingApi.delete({ query: { post_id: postId } })

    dispatch(resetPost({ postData: response.data.post }))
    return postSelectors.getById(getState(), postId)
  },
)

export const adjustPostSettingsForConfiguredChannels = createAsyncAction(
  'postActions/adjustPostSettingsForConfiguredChannels',
  async (
    {
      post,
      channelsInfo,
      builderId,
    }: {
      post: IPost
      channelsInfo: IBuilderChannelsInfo
      builderId: string
    },
    { dispatch, getState },
  ) => {
    if (!post) {
      return
    }

    const state = getState()
    const reasonId = builderSelectors.getFirstNodeReasonId(state, builderId)
    const messagingType = builderSelectors.getFirstNodeMessagingWindow(state, builderId)

    const firstAgentNodeWithChannelBeforeChannelNode =
      getFirstAgentNodeWithChannelBeforeChannelNode(state, builderId)

    const changes = getPostSettingsChangesAdjustedForConfiguredChannels(
      post,
      channelsInfo,
      reasonId,
      messagingType,
      firstAgentNodeWithChannelBeforeChannelNode?.channel,
    )

    if (!isEmpty(changes)) {
      dispatch(updatePostSettings({ postId: post.id, changes }))
    }
  },
)

export const adjustPostSettingsBeforeSchedule = createAsyncAction(
  'postActions/adjustPostSettingsBeforeSchedule',
  async (
    {
      post,
      channelsInfo,
    }: {
      post: IPost
      channelsInfo: IBuilderChannelsInfo
    },
    { dispatch },
  ) => {
    if (!post) {
      return
    }
    if (channelsInfo.hasContent) {
      return
    }

    const { messagingType, messageTag } = post

    const changes: {
      messagingType?: MessagingType
      messageTag?: MessageTagType | null
    } = {}

    const isDefaultsChanged = messagingType !== MessagingType.NON_PROMO || Boolean(messageTag)
    if (!channelsInfo.hasContent && isDefaultsChanged) {
      changes.messagingType = MessagingType.NON_PROMO
      changes.messageTag = null
    }

    if (!isEmpty(changes)) {
      dispatch(updatePostSettings({ postId: post.id, changes }))
    }
  },
)

export const updatePost = createAction(
  'postActions/updatePost',
  (postId: string | number, changes = {}) => {
    return {
      payload: {
        postId,
        changes,
      },
    }
  },
)

anotherTabNotificationsListener.on('post_updated', (data, dispatch) => {
  dispatch(resetPost({ postData: data.model }))
})
anotherTabNotificationsListener.on('post_created', (data, dispatch) => {
  dispatch(addPost(data.model))
})
anotherTabNotificationsListener.on('post_sending_stats', (data, dispatch) => {
  dispatch(
    updatePostSendingStats({
      postId: data.model.post_id,
      sendingStats: data.model.sending_stats,
    }),
  )
})
